import React from "react";

import type { SettingsTabId } from "./SettingsPageSkeleton";

const SHIMMER = "#e1e3e5";
const SHIMMER_ACTIVE = "#c9cccf";

const SETTINGS_TABS: Array<{ id: SettingsTabId; width: number }> = [
  { id: "EtsyChannelSetting", width: 110 },
  { id: "ProductSettings", width: 96 },
];

const ShimmerBar = ({
  width,
  height = 14,
  radius = 4,
  color = SHIMMER,
}: {
  width: number | string;
  height?: number;
  radius?: number;
  color?: string;
}) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      backgroundColor: color,
    }}
  />
);

const EtsyTabBootSkeleton = () => (
  <div
    style={{
      border: "1px solid #e1e3e5",
      borderRadius: 8,
      overflow: "hidden",
      padding: 10,
    }}
  >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)",
        gap: 16,
        alignItems: "start",
      }}
    >
      <div style={{ padding: "1.5rem 1.5rem 1.5rem 0" }}>
        <ShimmerBar width={160} height={22} />
        <div style={{ marginTop: 12, maxWidth: 260 }}>
          <ShimmerBar width="100%" height={14} />
          <div style={{ marginTop: 8 }}>
            <ShimmerBar width="88%" height={14} />
          </div>
        </div>
      </div>

      <div style={{ padding: "1.5rem 0 1.5rem 1.5rem" }}>
        <div
          style={{
            borderRadius: 12,
            backgroundColor: "#e4c496",
            padding: "18px 24px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 18,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 18, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "#f4f6f8",
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <ShimmerBar width={220} height={18} color="rgba(255,255,255,0.85)" />
              <div style={{ marginTop: 8 }}>
                <ShimmerBar width={48} height={12} color="rgba(255,255,255,0.75)" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
                <ShimmerBar width={70} height={12} color="rgba(255,255,255,0.75)" />
                <ShimmerBar width={62} height={12} color="rgba(255,255,255,0.75)" />
                <ShimmerBar width={92} height={12} color="rgba(255,255,255,0.75)" />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
            <ShimmerBar width={82} height={30} radius={10} color="rgba(255,255,255,0.78)" />
            <ShimmerBar width={154} height={30} radius={10} color="rgba(255,255,255,0.78)" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProductSettingsTabBootSkeleton = () => (
  <div style={{ padding: "1rem 0" }}>
    <ShimmerBar width={140} height={22} />
    <div
      style={{
        marginTop: 20,
        border: "1px solid #e1e3e5",
        borderRadius: 8,
        padding: 16,
        background: "#fff",
      }}
    >
      <ShimmerBar width={200} height={18} />
      <div style={{ marginTop: 12 }}>
        <ShimmerBar width="92%" height={14} />
        <div style={{ marginTop: 8 }}>
          <ShimmerBar width="78%" height={14} />
        </div>
      </div>
      <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <ShimmerBar width={18} height={18} radius={4} />
        <ShimmerBar width={200} height={14} />
      </div>
    </div>
  </div>
);

const GenericTabBootSkeleton = () => (
  <div style={{ padding: "1rem 0" }}>
    <ShimmerBar width={220} height={22} />
    <div style={{ marginTop: 16 }}>
      <ShimmerBar width="100%" height={120} radius={8} />
    </div>
    <div style={{ marginTop: 16 }}>
      <ShimmerBar width="100%" height={80} radius={8} />
    </div>
  </div>
);

interface SettingsBootSkeletonProps {
  selectedTab?: SettingsTabId;
  showPageChrome?: boolean;
}

/** Polaris-free settings skeleton for pre-AppProvider boot (FCP/LCP). */
export const SettingsBootSkeleton: React.FC<SettingsBootSkeletonProps> = ({
  selectedTab = "EtsyChannelSetting",
  // showPageChrome kept for API compat — top header button skeleton removed
  showPageChrome: _showPageChrome = true,
}) => (
  <div className="custom_layout_width">
    <div
      style={{
        background: "#fff",
        border: "1px solid #e1e3e5",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "nowrap",
          overflowX: "auto",
          borderBottom: "1px solid #e1e3e5",
          backgroundColor: "#ffffff",
        }}
      >
        {SETTINGS_TABS.map((tab) => {
          const isActive = selectedTab === tab.id;
          return (
            <div
              key={tab.id}
              style={{
                padding: "0.5rem 1rem",
                display: "flex",
                alignItems: "center",
                gap: 6,
                flex: "0 0 auto",
                borderBottom: isActive ? "2px solid #202223" : "2px solid transparent",
                background: isActive ? "#f6f6f7" : "transparent",
              }}
            >
              <ShimmerBar width={16} height={16} color={isActive ? SHIMMER_ACTIVE : SHIMMER} />
              <ShimmerBar width={tab.width} height={14} color={isActive ? SHIMMER_ACTIVE : SHIMMER} />
            </div>
          );
        })}
      </div>

      <div style={{ padding: 10 }}>
        {selectedTab === "EtsyChannelSetting" ? (
          <EtsyTabBootSkeleton />
        ) : selectedTab === "ProductSettings" ? (
          <ProductSettingsTabBootSkeleton />
        ) : (
          <GenericTabBootSkeleton />
        )}
      </div>
    </div>
  </div>
);

export default SettingsBootSkeleton;
